import { Controller } from "@hotwired/stimulus";
import { Properties } from "@posthog/types";

import { Current } from "~/utils/Current";

import Bugsnag from "~/lib/Bugsnag";

export default class extends Controller<HTMLElement> {
  static values = {
    loaded: Boolean,
    properties: Object,
    reset: Boolean,
  };

  declare loadedValue: boolean;
  declare propertiesValue: Properties | undefined;
  declare resetValue: boolean | undefined;

  connect(): void {
    // On Turbo navigations, posthog is already loaded but the
    // loaded callback from posthog.init won't fire again for
    // this new element. Self-trigger if posthog is ready.
    if (window.posthog?.__loaded) {
      this.loadedValue = true;
    }
  }

  // We call identify and reset here rather than connect so we
  // can ensure posthog is loaded. On initial page load, the
  // loaded value is set to true by the posthog.init loaded
  // callback. On Turbo navigations, connect() handles it above.
  loadedValueChanged(isLoaded: boolean) {
    if (isLoaded && this.resetValue) {
      this.reset();
    } else if (isLoaded) {
      this.identify();
    }
  }

  identify(): void {
    // we only initialise posthog for eligible users, and under
    // a feature flag - if it's not initialised, don't try
    // to track anything.
    if (!window.posthog) {
      return;
    }

    // there's no point calling identify without a distinct id,
    // but something has gone wrong if we try to
    if (!Current.user.uuid) {
      return Bugsnag.notify(new Error("Posthog identify call made without providing a user id"));
    }

    const distinctId = window.posthog?.get_distinct_id();

    // if posthog's distinct id is the current user's uuid, we've
    // already identified them, and don't need to do it again.
    if (distinctId === Current.user.uuid) {
      return;
    }

    const properties: Properties = this.propertiesValue || {};

    window.posthog.identify(Current.user.uuid, {
      email: properties.email,
      plan: properties.plan,
      org_slug: properties.org_slug,
    });
  }

  // When a user logs out, this unlinks future events
  // until they log back in. Primarily captures shared
  // computer use.
  reset(): void {
    if (!window.posthog) {
      return;
    }

    window.posthog.reset();
  }
}
